import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { v4 as uuid } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';

import { getStyles } from './styles';
import { Box, Button, CircularProgress, Divider, IconButton, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../../context/AppContext';
import getAnalyticsDataSetsData from '../../../../utils/ApiFunctions/AnalyticsDataSetsData';
import LoadBar from '../../../../utils/LoadBar';
import { MoreVert } from '@mui/icons-material';
import { ReactSVG } from 'react-svg';
import { IconSvg } from '../../../../utils/globalIcons';
import AnalyticsApi from '../../../../Api/Services/AmeyaBackend/AnalyticsToolApi';
import { tostAlert } from '../../../../utils/AlertToast';
import { fetchSubscriptionId, getUserName } from '../../../../utils/GetAccountDetails';
import MonitoringServiceApi from '../../../../Api/Services/AmeyaBackend/MonitoringServiceApi';
import { setAnalyticsDataSetsState } from '../../../../Redux/slice/dataSlice';
import { setAnalyticsDataSetAdded } from '../../../../Redux/slice/newDataSlice';
import AnalyticsDataSetsApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSets';
import moment from 'moment';

function Datasets() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();
    const { selectedWorkspace, selectedProject } = useAppContext();

    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [datasets, setDatasets] = useState([]);

    const deleteDatasetDetails = { datasetId: null, datasetAutoId: null, datasetAnchorEl: null }
    const [deleteDatasetData, setDeleteDatasetData] = useState(deleteDatasetDetails);
    // const [recallDatasetStatus, setRecallDatasetStatus] = useState(false)

    const analytics_data_sets = useSelector(state => state.all_data.analytics_data_sets);
    const analytics_data_set_added = useSelector(state => state.data_added.analytics_data_set_added);

    useEffect(() => {
        getAllData();
    }, [analytics_data_set_added]);

    useEffect(() => {
        const processingDatasets = datasets.filter(d => d?.payload?.status === "Processing");
        if (processingDatasets.length === 0) return;

        const interval = setInterval(async () => {
            try {
                let updatedItems = [];

                for (const item of processingDatasets) {
                    const result = await checkDatasetStatus([item]);
                    updatedItems.push(result[0]);
                }

                const updatedReduxSet = analytics_data_sets.map(p => updatedItems.find(u => u?.payload?.__auto_id__ === p?.payload?.__auto_id__) || p)
                dispatch(setAnalyticsDataSetsState(updatedReduxSet));
                setDatasets(prev => prev.map(p => updatedItems.find(u => u?.payload?.__auto_id__ === p?.payload?.__auto_id__) || p));

            } catch (err) {
                console.error("Polling Error:", err);
            }
        }, 7000);

        return () => clearInterval(interval);
    }, [datasets]);

    const checkJobStatus = async (datasetList = []) => {
        try {
            let updatedData = [...datasetList];

            for (let i = 0; i < updatedData.length; i++) {
                const item = updatedData[i];
                const source_details = item?.payload?.source_details || [];
                const source_types = item?.payload?.source_types ?? [];

                for (const sd of source_details) {
                    const job_id = sd?.job_id;
                    if (!job_id) continue;

                    try {
                        const monitor_res = await MonitoringServiceApi.getJobStatus(job_id);

                        if (monitor_res.status === 200) {
                            const job_status = monitor_res?.data?.status;
                            let currentSourceDetails = updatedData[i]?.payload?.source_details || [];

                            if (job_status === "Failed") {
                                updatedData[i] = {
                                    ...item,
                                    payload: {
                                        ...item.payload,
                                        monitoring_status: "failed",
                                        source_details: source_details.map(s =>
                                            s.job_id === job_id
                                                ? {
                                                    ...s,
                                                    error_message: "Job Failed",
                                                    error_status: true
                                                }
                                                : s
                                        )
                                    }
                                };
                            }

                            if (source_types.includes("links") && (job_status === "Success" || job_status === "Failed")) {

                                currentSourceDetails = updatedData[i]?.payload?.source_details || [];

                                updatedData[i] = {
                                    ...updatedData[i],
                                    payload: {
                                        ...updatedData[i].payload,
                                        source_details: currentSourceDetails.map(s =>
                                            s.job_id === job_id
                                                ? {
                                                    ...s,
                                                    all_links: (s.all_links || []).map(link => ({
                                                        ...link,
                                                        status:
                                                            job_status === "Success" ? "Success" : "Failed"
                                                    }))
                                                }
                                                : s
                                        )
                                    }
                                };
                            }

                        }
                    } catch (err) {
                        console.error("Error fetching monitoring status:", err);
                    }
                }
            }

            return updatedData;
        } catch (err) {
            console.error(err);
            return datasetList;
        }
    };

    const checkDatasetStatus = async (datasetData = []) => {
        try {
            let updatedData = [...datasetData];

            for (let index = 0; index < updatedData.length; index++) {
                let item = updatedData[index];

                const payload = item?.payload ?? {};
                const source_details = item?.payload?.source_details ?? [];
                const source_types = payload?.source_types ?? [];
                const dataset_item_id = payload?.__auto_id__ ?? null;
                const dataset_update_key = item?.update_key ?? null;
                const dataset_id = payload?.dataset_id ?? null;
                const dataset_status = payload?.status ?? "Processing";

                const skipTypes = ["mongodb", "ameya_extract", "food_recall"];
                if (source_types.some(type => skipTypes.includes(type))) continue;

                if (dataset_status === "Processing") {
                    const status_res = await AnalyticsApi.checkDatasetStatus(dataset_id);

                    if (status_res.status === 200) {
                        const statusData = status_res.data ?? {};
                        const message = statusData?.status ?? "Processing";
                        const returned_sources = statusData?.sources || [];

                        let updated_source_details = payload?.source_details || [];

                        if (returned_sources.includes("direct_files")) {
                            const all_direct_files = statusData?.data?.direct_files?.files || {};

                            updated_source_details = updated_source_details.map(sd => {
                                if (sd?.source_type === "files") {
                                    const updatedFiles = (sd.files || []).map(f => ({
                                        ...f,
                                        status: all_direct_files[f.file_name]?.status || "Processing",
                                        checksum: all_direct_files[f.file_name]?.checksum || null,
                                        new_file: false
                                    }));
                                    return { ...sd, files: updatedFiles };
                                }
                                return sd;
                            });
                        }


                        let finalStatus = dataset_status;

                        if (message === "Success" || message === "Failed") {

                            if (message === "Success") {
                                const reqObj = {
                                    "dataset_id": dataset_id,
                                }
                                await AnalyticsApi.createDatasetDescription(reqObj)
                            }

                            const dataObject = {
                                id: dataset_item_id,
                                fields: [
                                    { path: "$.status", value: message },
                                    { path: "$.source_details", value: updated_source_details }
                                ]
                            };
                            const update_response = await AnalyticsDataSetsApi.update(JSON.stringify(dataObject), dataset_item_id, dataset_update_key);
                            if (update_response.status === 200) {
                                finalStatus = message;
                            }
                        }

                        const updatedItem = {
                            ...item,
                            payload: {
                                ...payload,
                                status: finalStatus,
                                source_details: updated_source_details
                            }
                        };
                        updatedData[index] = updatedItem;
                    }
                }
            }

            return updatedData;
        } catch (error) {
            console.error(error);
            return datasetData;
        }
    };

    const getAllData = async () => {
        setLoading(true)
        try {

            let all_datasets = []
            if ((analytics_data_sets || [])?.length > 0 && analytics_data_set_added === false) {
                all_datasets = analytics_data_sets
            } else {
                const response = await getAnalyticsDataSetsData(selectedProject?.payload?.__auto_id__);
                all_datasets = response
            }

            const filtered_dataset = (all_datasets || []).map(item => {
                const source_types = item?.payload?.source_types || [];
                const skipSources = ["mongodb", "ameya_extract", "food_recall", "appflyte"];
                const hasSkipSource = source_types.some(type => skipSources.includes(type));

                let updatedStatus;
                if (hasSkipSource) {
                    updatedStatus = "Success";
                } else {
                    updatedStatus = item?.payload?.status ?? "Processing";
                }

                return {
                    ...item,
                    payload: {
                        ...item.payload,
                        monitoring_status: "success",
                        status: updatedStatus,
                    },
                };
            });


            const connector_datasets = filtered_dataset?.filter(item => !item?.payload?.source_types?.includes('food_recall'))
            const monitoredData = await checkJobStatus(connector_datasets);

            setDatasets(monitoredData);
            dispatch(setAnalyticsDataSetsState(monitoredData))
            dispatch(setAnalyticsDataSetAdded(false))

            // const is_food_recall = (filtered_dataset || []).some(item => item?.payload?.source_types?.includes('food_recall')) ?? false;
            // if (is_food_recall) {
            //     setRecallDatasetStatus(true);
            // }

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteDataset = async () => {
        try {
            setDeleteDatasetData(prev => ({ ...prev, datasetAnchorEl: null }));

            if (!window.confirm(intl.formatMessage(messages.delete_check))) {
                return;
            }

            setLoading(true);

            const { datasetId, datasetAutoId } = deleteDatasetData;
            const selectedDataset = datasets?.find(item => item?.payload?.dataset_id === datasetId);

            if (!selectedDataset) {
                throw new Error("Dataset not found");
            }

            await deleteDatasetByType(selectedDataset, datasetId);
            await deleteDatasetSettings(datasetAutoId);

            dispatch(setAnalyticsDataSetAdded(true))
            tostAlert(intl.formatMessage(messages.delete_success), 'success');

        } catch (error) {
            console.error('Error deleting dataset:', error);
            tostAlert(intl.formatMessage(messages.delete_error), 'error');
        } finally {
            setDeleteDatasetData(deleteDatasetDetails);
            setLoading(false);
        }
    };

    const deleteDatasetByType = async (selectedDataset, datasetId) => {

        const source_types = selectedDataset?.payload?.source_types ?? [];
        const hasMongo = source_types.find(item => (item === "mongodb" || item === "ameya_extract" || item === "appflyte"));

        if (hasMongo) {
            const response = await AnalyticsApi.deleteDataset(datasetId);
            if (response.status !== 200 && response.data.message !== "Data set not found or already deleted") {
                throw new Error("Failed to delete dataset");
            }
        }
        else {
            const source_details = selectedDataset?.payload?.source_details ?? [];
            const job_ids = (source_details || [])?.map(item => item?.job_id)
            if (job_ids.length === 0) {
                return;
            }

            let jobDeleteSuccess = false;
            let datasetDeleteSuccess = false;

            try {
                const response = await MonitoringServiceApi.deleteJobs(JSON.stringify({ "job_ids": job_ids }));
                if ([200, 201].includes(response.status)) {
                    jobDeleteSuccess = true;
                }
            } catch (error) {
                const failedDeletions = error?.response?.data?.detail?.failed_deletions ?? error?.response?.detail?.failed_deletions ?? [];
                const anyJobNotFound = failedDeletions.some((item) => item?.error === "Job not found" && item?.status === "not_found");
                if (anyJobNotFound) {
                    jobDeleteSuccess = true;
                }
            }

            try {
                const deleteDatasetResponse = await AnalyticsApi.deleteFileDataset(datasetId);
                if (deleteDatasetResponse.status === 200) {
                    datasetDeleteSuccess = true;
                }
            } catch (error) {
                console.log("error", error)
            }

            if (!jobDeleteSuccess && !datasetDeleteSuccess) {
                throw new Error("Failed to delete dataset jobs");
            }
            return true;

        }
    };

    const deleteDatasetSettings = async (datasetAutoId) => {
        const deleteResponse = await AnalyticsDataSetsApi.delete(datasetAutoId);
        if (deleteResponse.status !== 200) {
            throw new Error("Failed to update settings");
        }
    };

    const handleDatasetConfig = async (item) => {
        try {
            if (item) {
                navigate('/data-sets/edit', { state: { dataset_body: item } });
            } else {
                tostAlert(intl.formatMessage(messages.invalid_dataset), "warning")
            }
        } catch (error) {
            console.log(error)
        }
    }

    const handleAddFoodrecall = async () => {
        setDataLoading(true)
        try {

            const workspace_id = selectedWorkspace?.payload?.__auto_id__;
            const project_id = selectedProject?.payload?.__auto_id__ ?? null;
            const subscription_id = await fetchSubscriptionId();
            const source_id = `food_recall_${subscription_id}`
            const dataset_id = `food_recall_${uuid()}`
            const userName = getUserName();

            const datasetReqObj = {
                "dataset_name": 'Food Recall',
                "source_id": [source_id],
                "dataset_id": dataset_id
            }

            const datasetResponse = await AnalyticsApi.createDataset(JSON.stringify(datasetReqObj));
            if (datasetResponse.status !== 200) {
                tostAlert(intl.formatMessage(messages.subscribe_error), 'error')
                return;
            }

            const reqObj = {
                collection_item: {
                    dataset_id: dataset_id,
                    dataset_name: 'Food Recall',
                    excel_analytics_status: false,
                    source_types: ['food_recall'],
                    source_details: [source_id],
                    status: 'Success',
                    workspace_id,
                    project_id,
                    created_by: userName,
                    created_on: moment().format('YYYY-MM-DD HH:mm:ss')
                }
            }

            const add_response = await AnalyticsDataSetsApi.addData(JSON.stringify(reqObj));
            if (add_response.status === 200) {
                // setRecallDatasetStatus(true)
                dispatch(setAnalyticsDataSetAdded(true))
                tostAlert(intl.formatMessage(messages.subscribe_success), 'success')
            } else {
                tostAlert(intl.formatMessage(messages.subscribe_error), 'error')
            }
        } catch (error) {
            console.log(error)
        } finally {
            setDataLoading(false)
        }
    }

    const checkDatasetType = (item) => {
        try {
            const { payload } = item || {};
            const sourceTypes = payload?.source_types || [];

            if (sourceTypes.includes("mongodb") || sourceTypes.includes("food_recall") || sourceTypes.includes("ameya_extract") || sourceTypes.includes("appflyte")) {
                return null;
            }

            return payload?.excel_analytics_status ? "excel" : "files";
        } catch (error) {
            console.error("Error in checkDatasetType:", error);
            return null;
        }
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>
                {(loading || dataLoading) && <LoadBar />}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>My Datasets</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            onClick={(e) => navigate('/data-sets/datasource')}
                            disabled={(loading || dataLoading)}
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '120px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                Add Dataset
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ marginTop: '30px' }}>
                    {loading ?
                        (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '100px' }}>
                            <CircularProgress />
                        </Box>)
                        :
                        (datasets || [])?.length === 0 && !loading ?
                            (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', marginTop: '100px' }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
                                    No Dataset Available
                                </Typography>
                            </Box>)
                            :
                            (<Box
                                sx={{
                                    marginTop: '30px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                                    gap: '40px',
                                }}
                            >
                                {(datasets || [])?.length > 0 && (
                                    datasets.map((item, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                bgcolor: '#FFFFFF',
                                                borderRadius: '10px',
                                                border: `1px solid ${item?.payload?.monitoring_status === "success" ? '#DEDEDE' : '#C30E2E'}`
                                            }}
                                        >
                                            <Box padding={'20px'}>
                                                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>

                                                    <Tooltip
                                                        title={
                                                            <>
                                                                {item?.payload?.dataset_name}
                                                                {checkDatasetType(item) && ` (${checkDatasetType(item)})`}
                                                            </>
                                                        }
                                                        arrow
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: '15px',
                                                                fontWeight: 600,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                maxWidth: '250px',
                                                            }}
                                                        >
                                                            {item?.payload?.dataset_name || ""}
                                                            {checkDatasetType(item) ? ` (${checkDatasetType(item)})` : ""}
                                                        </Typography>
                                                    </Tooltip>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                        <IconButton
                                                            onClick={(e) => setDeleteDatasetData({
                                                                ...deleteDatasetData,
                                                                datasetAnchorEl: e.currentTarget,
                                                                datasetId: item?.payload?.dataset_id,
                                                                datasetAutoId: item?.payload?.__auto_id__,
                                                            })}
                                                        >
                                                            <MoreVert />
                                                        </IconButton>
                                                    </Box>

                                                </Box>
                                                <Box marginTop={'10px'} >
                                                    <Typography sx={{ fontSize: '11px', fontWeight: '600' }}>
                                                        SOURCES
                                                    </Typography>

                                                    <Box marginTop="10px" display="flex" gap="8px" alignItems="center">
                                                        {Array.isArray(item?.payload?.source_types) &&
                                                            (item?.payload?.source_types || [])?.map((source, idx) => (
                                                                <ReactSVG
                                                                    key={idx}
                                                                    src={{
                                                                        google_drive: IconSvg.driveIcon,
                                                                        one_drive: IconSvg.onedriveIcon,
                                                                        aws_s3: IconSvg.awsIcon,
                                                                        links: IconSvg.wwwIcon,
                                                                        ameya_extract: IconSvg.ameyaIcon,
                                                                        mongodb: IconSvg.mongoIcon,
                                                                        gmail: IconSvg.mailIcon,
                                                                        outlook: IconSvg.mailIcon,
                                                                        files: IconSvg.fileSourceIcon,
                                                                        food_recall: IconSvg.fastFoodIcon,
                                                                        appflyte: IconSvg.apiIcon
                                                                    }[source]}
                                                                    beforeInjection={(svg) => {
                                                                        svg.setAttribute('style', 'width:18px; height:18px; display:block;');
                                                                        svg.setAttribute('stroke', '#000000');
                                                                    }}
                                                                />
                                                            ))}
                                                    </Box>

                                                </Box>

                                            </Box>
                                            <Divider sx={{ backgroundColor: `${item?.payload?.monitoring_status !== "success" && '#C30E2E'}`, boxShadow: 'none' }} />
                                            <Box
                                                sx={{
                                                    padding: '15px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: item?.payload?.status === "Processing" ? "space-between" : "center",
                                                    height: '30px',
                                                    "&:hover": {
                                                        borderBottomLeftRadius: '10px',
                                                        borderBottomRightRadius: '10px',
                                                        cursor: 'pointer'
                                                    }
                                                }}
                                            >
                                                <Button
                                                    variant='text'
                                                    disableRipple
                                                    onClick={() => handleDatasetConfig(item)}
                                                    disabled={loading || dataLoading}
                                                    sx={{
                                                        all: 'unset',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <Box>
                                                        <ReactSVG
                                                            src={item?.payload?.monitoring_status === "success"
                                                                ? IconSvg.configureIcon
                                                                : IconSvg.errorDataIcon
                                                            }
                                                            beforeInjection={(svg) => {
                                                                svg.setAttribute('style', 'width:18px; height:18px; display:block;');
                                                                svg.setAttribute(
                                                                    'stroke',
                                                                    item?.payload?.monitoring_status === "success" ? '#0B51C5' : '#C30E2E'
                                                                );
                                                            }}
                                                        />
                                                    </Box>

                                                    <Typography
                                                        sx={{
                                                            ml: '5px',
                                                            fontSize: '12px',
                                                            fontWeight: 400,
                                                            color: item?.payload?.monitoring_status === "success" ? '#0B51C5' : '#C30E2E',
                                                            '&:hover': { textDecoration: 'underline' }
                                                        }}
                                                    >
                                                        {item?.payload?.monitoring_status === "success"
                                                            ? 'Configure'
                                                            : 'Data source failure, review now'}
                                                    </Typography>
                                                </Button>

                                                {item?.payload?.status === "Processing" && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }} >
                                                        <ReactSVG
                                                            src={{
                                                                Processing: IconSvg.trainingIcon,
                                                                Success: IconSvg.trainedIcon,
                                                                Error: IconSvg.errorIcon
                                                            }[item?.payload?.status]}
                                                            beforeInjection={(svg) => {
                                                                svg.setAttribute('style', 'width:18px; height:18px; display:block;');
                                                            }}
                                                        />
                                                        <Typography sx={{ fontSize: '12px' }}>
                                                            Processing sources ...
                                                        </Typography>
                                                    </Box>
                                                )}

                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Box>)
                    }
                </Box>


                {/* {!loading && (<Box sx={{ marginTop: '40px' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>AI Models Hub</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: '400', marginTop: '5px' }}>Ready to use AI models created and managed by Ameya</Typography>

                    <Box sx={{ marginTop: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '40px' }}>
                        <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #DEDEDE' }}>
                            <Box padding={'20px'}>

                                <ReactSVG
                                    src={IconSvg.fastFoodIcon}
                                    beforeInjection={(svg) => {
                                        svg.setAttribute('style', 'width:48px; height:48px; display:block;');
                                    }}
                                />
                                <Typography sx={{ fontSize: '15px', fontWeight: '600' }}>Food Recall</Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: '500', marginTop: '10px' }}>
                                    Data analysis and predictive modeling to<br />
                                    anticipate potential food safety issues that might<br />
                                    lead to a recall.
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ padding: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '30px' }}>
                                <Button
                                    variant='text'
                                    disableRipple
                                    onClick={handleAddFoodrecall}
                                    disabled={loading || dataLoading || recallDatasetStatus}
                                    sx={{
                                        all: 'unset', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: !recallDatasetStatus ? 'pointer' : 'default'
                                    }}
                                >
                                    <Box>
                                        <ReactSVG
                                            src={IconSvg.configureIcon}
                                            beforeInjection={(svg) => {
                                                svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                svg.setAttribute('stroke', '#000000');
                                            }}
                                        />
                                    </Box>
                                    <Typography sx={{
                                        marginLeft: '5px', fontSize: '12px', fontWeight: '400', color: '#0B51C5',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}>
                                        {!recallDatasetStatus ? 'Subscribe and Add' : 'Subscibed'}
                                    </Typography>
                                </Button>
                            </Box>
                        </Box>
                    </Box>

                </Box>)} */}

            </Box>

            <Popover
                open={Boolean(deleteDatasetData.datasetAnchorEl)}
                anchorEl={deleteDatasetData.datasetAnchorEl}
                onClose={() => setDeleteDatasetData({ ...deleteDatasetData, datasetAnchorEl: null, datasetId: null, datasetAutoId: null })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ borderRadius: '5px' }}
            >
                <Box minWidth={150}>
                    <MenuItem onClick={handleDeleteDataset}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ReactSVG src={IconSvg.deleteIcon} beforeInjection={(svg) => {
                                svg.setAttribute('style', 'width: 18x; height: 18px;display: block;');
                                svg.setAttribute('stroke', '#C30E2E');
                            }} />
                            <Typography sx={{ fontSize: '14px', fontWeight: '400', marginLeft: '10px' }}>Delete</Typography>
                        </Box>
                    </MenuItem>
                </Box>
            </Popover>

        </Box >
    )
}

export default Datasets;
